import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { Button } from 'components/index';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LineItemUnitEnum, LineItemLabelEnum } from '../../glossary/glossary.type';
import { Close } from '@mui/icons-material';
import SelectInput from '../../SelectInput';
import { LineItemDTO, ProjectLineItemDTO } from '../project.type';
import { CreateDrawLineItemDTO } from './project-draws.type';
import SelectDivision from '../../glossary/SelectDivision';
import NumericFormatInput from '../../general/NumericFormatInput';
import { useLineItemSearch } from '../../../hooks/useLineItemSearch';
import DescriptionGlossaryField from './DescriptionGlossaryField';

interface EditDrawLineItemDialogProps {
  open: boolean;
  onClose: () => void;
  projectDrawId: string;
  lineItem: ProjectLineItemDTO | null;
  setValues: (values: CreateDrawLineItemDTO) => void;
}

const unitsList = Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit }));

const EditDrawLineItemDialog: React.FC<EditDrawLineItemDialogProps> = ({ open, onClose, projectDrawId, lineItem, setValues }) => {
  const { lineItemOptions, isSearching, searchLineItems, addCreatedOption } = useLineItemSearch(open);

  const [descriptionInput, setDescriptionInput] = React.useState(lineItem?.lineItem.description || '');
  const [committed, setCommitted] = React.useState<LineItemDTO | null>(
    lineItem ? { id: lineItem.id, description: lineItem.lineItem.description } : null,
  );
  const initialFormValues = {
    id: lineItem?.id || '',
    projectDrawId: lineItem?.projectDrawId || projectDrawId,
    description: lineItem?.lineItem.description || '',
    unit: lineItem?.lineItem.unit || unitsList[0].value,
    divisionId: lineItem?.lineItem.division?.id || null,
    originalBudget: lineItem?.originalBudget || 0,
    changeOrder: lineItem?.changeOrder || 0,
    reallocation: lineItem?.reallocation || 0,
    previousApplication: lineItem?.previousApplication || 0,
    completeThisPeriod: lineItem?.completeThisPeriod || 0,
    storedThisPeriod: lineItem?.storedThisPeriod || 0,
    quantity: lineItem?.quantity || 0,
    retainage: lineItem?.retainage || 0,
    isChangeOrder: (lineItem?.labels || []).includes(LineItemLabelEnum.ChangeOrder),
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      description: yup.string().required('Description is required'),
      unit: yup.string().required('Unit is required'),
      originalBudget: yup.number().required('Budget is required'),
      changeOrder: yup.number().required('Change Order is required'),
      reallocation: yup.number().required('Reallocation is required'),
      previousApplication: yup.number().required('Previous Application is required'),
      completeThisPeriod: yup.number().required('Complete This Period is required'),
      storedThisPeriod: yup.number().required('Stored This Period is required'),
      quantity: yup.number().required('Quantity is required'),
      retainage: yup.number().required('Retainage is required'),
      divisionId: yup.string().nullable(),
    }),
    onSubmit: async values => {
      const { isChangeOrder, ...rest } = values;
      const baseLabels = lineItem?.labels ?? [];
      const labelSet = new Set(baseLabels);
      if (isChangeOrder) {
        labelSet.add(LineItemLabelEnum.ChangeOrder);
      } else {
        labelSet.delete(LineItemLabelEnum.ChangeOrder);
      }
      const nextLabels = Array.from(labelSet);
      setValues({
        ...rest,
        originalBudget: Number(rest.originalBudget) || 0,
        changeOrder: Number(rest.changeOrder) || 0,
        reallocation: Number(rest.reallocation) || 0,
        previousApplication: Number(rest.previousApplication) || 0,
        completeThisPeriod: Number(rest.completeThisPeriod) || 0,
        storedThisPeriod: Number(rest.storedThisPeriod) || 0,
        quantity: Number(rest.quantity) || 0,
        retainage: Number(rest.retainage) || 0,
        labels: nextLabels.length ? nextLabels : undefined,
      });
      formik.resetForm();
      onClose();
    },
  });

  // Dialog stays mounted, so reset state on open from the current `lineItem`.
  React.useEffect(() => {
    if (open) {
      setDescriptionInput(lineItem?.lineItem.description || '');
      setCommitted(lineItem ? { id: lineItem.id, description: lineItem.lineItem.description } : null);
    } else {
      formik.resetForm();
    }
  }, [open, lineItem]);

  const exactMatch = committed ? null : (lineItemOptions.find(option => option.isExactMatch) ?? null);
  const isBlockedByExactMatch = !!exactMatch;

  const commitExisting = (option: LineItemDTO) => {
    setCommitted(option);
    setDescriptionInput(option.description);
    formik.setFieldValue('description', option.description);
    formik.setFieldValue('divisionId', option.divisionId ?? null);
    if (option.unit) formik.setFieldValue('unit', option.unit);
  };

  const commitNew = (created: LineItemDTO) => {
    addCreatedOption(created);
    commitExisting(created);
  };

  const handleDescriptionInputChange = (newInputValue: string) => {
    setDescriptionInput(newInputValue);
    setCommitted(null);
    formik.setFieldValue('description', '');
    searchLineItems(newInputValue);
  };

  const renderGlossaryInputs = () => (
    <Box>
      <Stack spacing={1.5}>
        <DescriptionGlossaryField
          descriptionInput={descriptionInput}
          onDescriptionInputChange={handleDescriptionInputChange}
          options={lineItemOptions}
          isSearching={isSearching}
          selectedOption={committed}
          onSelectExisting={commitExisting}
          onSaveNew={commitNew}
        />

        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ flex: '0 0 50%' }}>
            <SelectDivision
              selectedDivision={formik.values.divisionId}
              setSelectedDivision={(divisionId: string | null) => formik.setFieldValue('divisionId', divisionId)}
            />
          </Box>
          <Box sx={{ flex: '0 0 20%' }}>
            <SelectInput
              label='Unit'
              selected={formik.values.unit}
              setSelected={unit => formik.setFieldValue('unit', unit)}
              list={unitsList}
            />
          </Box>
          <FormControlLabel
            sx={{ flex: '0 0 30%', mr: 0 }}
            control={
              <Switch
                checked={formik.values.isChangeOrder}
                onChange={(_, checked) => formik.setFieldValue('isChangeOrder', checked)}
                name='isChangeOrder'
                color='primary'
                size='small'
              />
            }
            label='Mark as Change Order'
            componentsProps={{
              typography: { variant: 'body2' },
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {lineItem ? `Edit Line Item : ${lineItem.lineItem.description}` : 'Add Line Item'}
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={2}>
            {renderGlossaryInputs()}

            {/* Budget */}
            <TextField
              label='Original Budget'
              fullWidth
              variant='standard'
              value={formik.values.originalBudget}
              onChange={formik.handleChange}
              name='originalBudget'
              error={formik.touched.originalBudget && Boolean(formik.errors.originalBudget)}
              helperText={formik.touched.originalBudget && formik.errors.originalBudget}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />

            {/* Change Order */}
            <TextField
              label='Change Order'
              fullWidth
              variant='standard'
              value={formik.values.changeOrder}
              onChange={formik.handleChange}
              name='changeOrder'
              error={formik.touched.changeOrder && Boolean(formik.errors.changeOrder)}
              helperText={formik.touched.changeOrder && formik.errors.changeOrder}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />

            {/* Reallocation */}
            <TextField
              label='Reallocation'
              fullWidth
              variant='standard'
              value={formik.values.reallocation}
              onChange={formik.handleChange}
              name='reallocation'
              error={formik.touched.reallocation && Boolean(formik.errors.reallocation)}
              helperText={formik.touched.reallocation && formik.errors.reallocation}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />

            {/* Previous Application */}
            <TextField
              label='Previous Application'
              fullWidth
              variant='standard'
              value={formik.values.previousApplication}
              onChange={formik.handleChange}
              name='previousApplication'
              error={formik.touched.previousApplication && Boolean(formik.errors.previousApplication)}
              helperText={formik.touched.previousApplication && formik.errors.previousApplication}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />

            {/* Complete This Period */}
            <TextField
              label='Complete This Period'
              fullWidth
              variant='standard'
              value={formik.values.completeThisPeriod}
              onChange={formik.handleChange}
              name='completeThisPeriod'
              error={formik.touched.completeThisPeriod && Boolean(formik.errors.completeThisPeriod)}
              helperText={formik.touched.completeThisPeriod && formik.errors.completeThisPeriod}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />

            {/* Stored This Period */}
            <TextField
              label='Stored This Period'
              fullWidth
              variant='standard'
              value={formik.values.storedThisPeriod}
              onChange={formik.handleChange}
              name='storedThisPeriod'
              error={formik.touched.storedThisPeriod && Boolean(formik.errors.storedThisPeriod)}
              helperText={formik.touched.storedThisPeriod && formik.errors.storedThisPeriod}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />
            <TextField
              label='Quantity'
              fullWidth
              variant='standard'
              value={formik.values.quantity}
              onChange={formik.handleChange}
              name='quantity'
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
              helperText={formik.touched.quantity && formik.errors.quantity}
              InputProps={{
                inputComponent: NumericFormatInput as any,
              }}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={() => formik.submitForm()}
          disabled={!formik.isValid || !formik.dirty || isBlockedByExactMatch || (!lineItem && !committed)}
        >
          {lineItem ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDrawLineItemDialog;
